import { ReactNode, useEffect, useState, useRef } from 'react';
import css from './Tooltip.module.css';

export interface TooltipProps {
  children: ReactNode;
  text: string | ReactNode;
  delay?: number;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'cursor';
}

export default function Tooltip({ 
  children, 
  text, 
  delay = 300,
  position = 'top'
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const timerRef = useRef<number | null>(null);
  
  const handleMouseEnter = (e: React.MouseEvent) => {
    // Запускаем таймер при наведении
    timerRef.current = window.setTimeout(() => {
      setIsVisible(true);
    }, delay);
  };
  
  const handleMouseMove = (e: React.MouseEvent) => {
    if (position === 'cursor') {
      // Устанавливаем координаты по положению курсора
      setCoords({ x: e.clientX, y: e.clientY });
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      
      // Рассчитываем позицию в зависимости от выбранного положения
      let x = 0;
      let y = 0;
      
      switch(position) {
        case 'top':
          x = rect.left + rect.width / 2;
          y = rect.top;
          break;
        case 'bottom':
          x = rect.left + rect.width / 2;
          y = rect.bottom;
          break;
        case 'left':
          x = rect.left;
          y = rect.top + rect.height / 2;
          break;
        case 'right':
          x = rect.right;
          y = rect.top + rect.height / 2;
          break;
      }
      
      setCoords({ x, y });
    }
  };
  
  const handleMouseLeave = () => {
    // Очищаем таймер при уходе курсора
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsVisible(false);
  };
  
  // Очистка таймера при размонтировании компонента
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);
  
  return (
    <div 
      className={css.tooltip_container} 
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      style={{ cursor: 'help' }}
    >
      {children}
      {isVisible && (
        <div 
          className={`${css.tooltip} ${position !== 'cursor' ? css[`tooltip_${position}`] : ''}`}
          style={{
            position: 'fixed',
            left: `${coords.x}px`,
            top: `${coords.y}px`,
            transform: position !== 'cursor' ? getTransform(position) : 'translate(10px, 10px)'
          }}
        >
          {text}
        </div>
      )}
    </div>
  );
}

function getTransform(position: string): string {
  switch(position) {
    case 'top':
      return 'translate(-50%, -100%)';
    case 'bottom':
      return 'translate(-50%, 10px)';
    case 'left':
      return 'translate(-100%, -50%)';
    case 'right':
      return 'translate(10px, -50%)';
    default:
      return 'translate(-50%, -100%)';
  }
} 